import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Tab, Tabs, Alert } from 'react-bootstrap';
import { useNotification } from '../hooks/useNotification';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { apiClient } from '../utils/apiUtils';
import { SkeletonForm, SkeletonSelect, SkeletonTextarea } from './LoadingSkeleton';
import SelectField from './SelectField';
import LineItemBudgetModal from './LineItemBudgetModal';

const ProjectProposalEditModal = ({ show, onHide, projectId, onSuccess }) => {
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteLibModal, setShowDeleteLibModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [updatedProjectData, setUpdatedProjectData] = useState(null);
  const [originalFundSource, setOriginalFundSource] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [fundSourceOther, setFundSourceOther] = useState('');
  const [users, setUsers] = useState([]);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [signatoryPrefs, setSignatoryPrefs] = useState({
    prepared_by_enabled: true,
    reviewed_by_enabled: true,
    ra_1_enabled: true,
    ra_2_enabled: true,
    approved_by_enabled: true
  });

  const toggleSignatoryPref = (key) => {
    setSignatoryPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    requestType: '',
    eventType: '',
    extensionAgenda: '',
    sdg: [],
    officesInvolved: '',
    programsInvolved: '',
    partnerAgencies: '',
    beneficiaries: '',
    selectedProjectLeader: '',
    projectLeader: '',
    projectLeaderHonorific: '',
    projectLeaderTasks: [''],
    assistantLeaders: [{ name: '', honorific: '', tasks: [''] }],
    coordinators: [{ name: '', honorific: '', tasks: [''] }],
    projectStaffs: [{ name: '', honorific: '', tasks: [''] }],
    rationale: '',
    objectives: '',
    expectedOutput: '',
    strategiesMethods: '',
    functionalRelationships: '',
    totalCost: '',
    fundSource: '',
    financialPlanDetails: '',
    sustainabilityPlan: '',
    status: ''
  });

  // Financial Plan rows (editable table)
  const [financialPlanRows, setFinancialPlanRows] = useState([
    { description: '', quantity: '', unit: '', price: '', total: '' },
    { description: '', quantity: '', unit: '', price: '', total: '' },
    { description: '', quantity: '', unit: '', price: '', total: '' },
    { description: '', quantity: '', unit: '', price: '', total: '' }
  ]);

  // M&E free-text rows
  const makeEmptyMeRow = () => ({ 
    objectives: '', 
    performance_indicators: '', 
    baseline_data: '', 
    performance_target: '', 
    data_source: '', 
    collection_method: '', 
    frequency: '', 
    responsible: '' 
  });
  const [meRows, setMeRows] = useState([
    makeEmptyMeRow(), 
    makeEmptyMeRow(), 
    makeEmptyMeRow(), 
    makeEmptyMeRow(), 
    makeEmptyMeRow()
  ]);
  const addMeRow = () => setMeRows(prev => [...prev, makeEmptyMeRow()]);
  const removeMeRow = () => setMeRows(prev => prev.length > 5 ? prev.slice(0, prev.length - 1) : prev);

  // Function to format number with commas and decimal places
  const formatNumberWithCommas = (value) => {
    if (!value) return '';
    const cleanValue = String(value).replace(/[^\d.]/g, '');
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (decimalPart !== undefined) {
      const limitedDecimal = decimalPart.substring(0, 2);
      return formattedInteger + '.' + limitedDecimal;
    }
    return formattedInteger;
  };

  // Function to remove commas from number (keep decimal places)
  const removeCommas = (value) => {
    return String(value).replace(/,/g, '');
  };

  // Financial Plan functions
  const addFinancialPlanRow = () => {
    setFinancialPlanRows(prev => {
      const next = [...prev, { description: '', quantity: '', unit: '', price: '', total: '' }];
      setFormData(fd => ({ ...fd, financialPlanDetails: JSON.stringify(next) }));
      return next;
    });
  };

  const removeFinancialPlanRow = () => {
    setFinancialPlanRows(prev => {
      if (prev.length <= 4) return prev; // keep minimum 4
      const next = prev.slice(0, prev.length - 1);
      setFormData(fd => ({ ...fd, financialPlanDetails: JSON.stringify(next) }));
      return next;
    });
  };

  const updateFinancialRow = (index, key, value) => {
    setFinancialPlanRows(prev => {
      const sanitizeNumber = (v) => {
        if (v === null || v === undefined) return '';
        const cleaned = String(v).replace(/[^\d.]/g, '');
        const parts = cleaned.split('.');
        if (parts.length > 2) {
          return parts[0] + '.' + parts.slice(1).join('');
        }
        return cleaned;
      };
      const next = prev.map((r, i) => {
        if (i !== index) return r;
        if (key === 'price' || key === 'total') {
          return { ...r, [key]: sanitizeNumber(value) };
        }
        return { ...r, [key]: value };
      });
      // keep JSON in formData for backend
      setFormData(fd => ({ ...fd, financialPlanDetails: JSON.stringify(next) }));
      return next;
    });
  };

  const getFinancialPlanGrandTotal = () => {
    return financialPlanRows.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0);
  };

  // Fetch users from API
  useEffect(() => {
    if (show) {
      const fetchUsers = async () => {
        try {
          const response = await apiClient.get('/users/eligible-participants');
          setUsers(response.data);
        } catch (error) {
          console.error('Error fetching users:', error);
          showError('Failed to load users. Please refresh the page.');
          setUsers([]);
        }
      };
      fetchUsers();
    }
  }, [show, showError]);

  // Load project data when modal opens
  useEffect(() => {
    if (show && projectId) {
      const loadProject = async () => {
        try {
          setLoading(true);
          const { data } = await axios.get(`/projects/${projectId}`);
          
          // Parse existing data
          const parseList = (value) => { 
            try { 
              const v = JSON.parse(value); 
              return Array.isArray(v) ? v : []; 
            } catch { 
              return []; 
            } 
          };

          // Parse fund source and extract "Others" text
          let fundSourceValue = '';
          let othersText = '';
          
          let parsedFundSource = data.fund_source;
          if (typeof data.fund_source === 'string' && data.fund_source.startsWith('[')) {
            try {
              parsedFundSource = JSON.parse(data.fund_source);
            } catch (e) {
              console.log('Failed to parse fund_source JSON:', e);
            }
          }
          
          if (Array.isArray(parsedFundSource)) {
            fundSourceValue = parsedFundSource.find(fs => fs && fs.trim()) || '';
          } else if (typeof parsedFundSource === 'string') {
            fundSourceValue = parsedFundSource;
          }
          
          if (fundSourceValue && fundSourceValue.startsWith('Others:')) {
            othersText = fundSourceValue.replace('Others:', '').trim();
            fundSourceValue = 'Others';
          }

          // Load financial plan details
          let financialPlanRowsData = [
            { description: '', quantity: '', unit: '', price: '', total: '' },
            { description: '', quantity: '', unit: '', price: '', total: '' },
            { description: '', quantity: '', unit: '', price: '', total: '' },
            { description: '', quantity: '', unit: '', price: '', total: '' }
          ];
          if (data.financial_plan_details) {
            try {
              const parsed = typeof data.financial_plan_details === 'string' 
                ? JSON.parse(data.financial_plan_details) 
                : data.financial_plan_details;
              if (Array.isArray(parsed) && parsed.length > 0) {
                // Ensure at least 4 rows
                while (parsed.length < 4) {
                  parsed.push({ description: '', quantity: '', unit: '', price: '', total: '' });
                }
                financialPlanRowsData = parsed.map(r => ({
                  description: r.description || '',
                  quantity: r.quantity || '',
                  unit: r.unit || '',
                  price: r.price || '',
                  total: r.total || ''
                }));
              }
            } catch (e) {
              console.log('Failed to parse financial_plan_details:', e);
            }
          }

          // Load monitoring and evaluation data
          let meRowsData = [
            makeEmptyMeRow(), 
            makeEmptyMeRow(), 
            makeEmptyMeRow(), 
            makeEmptyMeRow(), 
            makeEmptyMeRow()
          ];
          const monitoringData = data.monitoring_evaluation_table || data.monitoring_evaluation;
          if (monitoringData) {
            try {
              const parsed = typeof monitoringData === 'string' 
                ? JSON.parse(monitoringData) 
                : monitoringData;
              if (typeof parsed === 'object' && parsed !== null) {
                // New: free rows model (preferred)
                if (Array.isArray(parsed.rows)) {
                  const ensureFive = (arr) => {
                    const copy = [...arr];
                    while (copy.length < 5) copy.push(makeEmptyMeRow());
                    return copy;
                  };
                  meRowsData = ensureFive(parsed.rows);
                }
              }
            } catch (e) {
              console.log('Could not parse monitoring_evaluation_table as JSON:', e);
            }
          }

          const formatTimeForInput = (timeString) => {
            if (!timeString) return '';
            if (timeString.match(/^\d{2}:\d{2}$/)) return timeString;
            if (timeString.match(/^\d{2}:\d{2}:\d{2}$/)) return timeString.substring(0, 5);
            return timeString;
          };
          
          // Map database values to form values
          const mapRequestType = (dbValue) => {
            if (dbValue === 'Client Requested') return 'client';
            if (dbValue === 'Department Initiative') return 'department';
            return dbValue || '';
          };
          
          const mapEventType = (dbValue) => {
            if (dbValue === 'Program') return 'Program';
            if (dbValue === 'Project') return 'Project';
            if (dbValue === 'Activity') return 'Activity';
            return dbValue || '';
          };
          
          // Parse extension agenda - might be stored as array or string
          let extensionAgenda = data.extension_agenda || '';
          if (Array.isArray(extensionAgenda)) {
            extensionAgenda = extensionAgenda[0] || '';
          }
          if (typeof extensionAgenda === 'string' && extensionAgenda.startsWith('[')) {
            try {
              const parsed = JSON.parse(extensionAgenda);
              extensionAgenda = parsed[0] || '';
            } catch (e) {
              // Could not parse extension agenda JSON
            }
          }
          
          // Format dates for input fields (add 1 day to compensate for timezone)
          const formatDateForInput = (dateString) => {
            if (!dateString) return '';
            const date = new Date(dateString);
            date.setDate(date.getDate() + 1);
            return date.toISOString().split('T')[0];
          };
          
          // Helper to extract honorific and name from a full name string
          const extractHonorificAndName = (fullName) => {
            if (!fullName) return { honorific: '', name: '' };
            // Remove task if present (format: "Name - Task" or "Honorific Name - Task")
            const nameWithoutTask = fullName.split(' - ')[0].trim();
            // Check for common honorifics at the start
            const honorificPattern = /^(Mr\.|Mrs\.|Ms\.|Dr\.|Engr\.|Prof\.|Assoc\. Prof\.|Atty\.|Rev\.)\s+(.+)$/i;
            const match = nameWithoutTask.match(honorificPattern);
            if (match) {
              return { honorific: match[1], name: match[2].trim() };
            }
            return { honorific: '', name: nameWithoutTask };
          };

          // Load project leader
          let projectLeaderId = '';
          let projectLeaderName = '';
          let projectLeaderHonorific = '';
          if (data.project_leader_id && data.project_leader_name) {
            projectLeaderId = data.project_leader_id;
            const { honorific, name } = extractHonorificAndName(data.project_leader_name);
            projectLeaderName = name;
            projectLeaderHonorific = honorific;
          }

          // Load assistant project leaders
          let assistantLeaders = [{ name: '', honorific: '', tasks: [''] }];
          if (data.assistant_project_leader_names) {
            try {
              let namesArray = [];
              const v = typeof data.assistant_project_leader_names === 'string' && data.assistant_project_leader_names.trim().startsWith('[')
                ? JSON.parse(data.assistant_project_leader_names)
                : data.assistant_project_leader_names;
              namesArray = Array.isArray(v) ? v : [];
              
              let tasksObj = {};
              if (data.assistant_project_leader_tasks) {
                try {
                  const t = typeof data.assistant_project_leader_tasks === 'string' && data.assistant_project_leader_tasks.trim().startsWith('{')
                    ? JSON.parse(data.assistant_project_leader_tasks)
                    : data.assistant_project_leader_tasks;
                  tasksObj = typeof t === 'object' && t !== null ? t : {};
                } catch {
                  tasksObj = {};
                }
              }
              
              let idsArray = [];
              if (data.assistant_project_leader_ids) {
                try {
                  const i = typeof data.assistant_project_leader_ids === 'string' && data.assistant_project_leader_ids.trim().startsWith('[')
                    ? JSON.parse(data.assistant_project_leader_ids)
                    : data.assistant_project_leader_ids;
                  idsArray = Array.isArray(i) ? i : [];
                } catch {
                  idsArray = [];
                }
              }
              
              assistantLeaders = namesArray.map((name, index) => {
                const userId = idsArray[index] || '';
                const taskStr = tasksObj[userId] || tasksObj[`assistant_${index}`] || '';
                const tasks = taskStr ? taskStr.split(';').map(t => t.trim()).filter(t => t) : [''];
                const { honorific, name: cleanName } = extractHonorificAndName(name);
                return { name: cleanName, honorific, tasks: tasks.length > 0 ? tasks : [''] };
              });
              
              if (assistantLeaders.length === 0) {
                assistantLeaders = [{ name: '', honorific: '', tasks: [''] }];
              }
            } catch {
              assistantLeaders = [{ name: '', honorific: '', tasks: [''] }];
            }
          }

          // Load coordinators
          let coordinators = [{ name: '', honorific: '', tasks: [''] }];
          if (data.coordinator_names) {
            try {
              let namesArray = [];
              const v = typeof data.coordinator_names === 'string' && data.coordinator_names.trim().startsWith('[')
                ? JSON.parse(data.coordinator_names)
                : data.coordinator_names;
              namesArray = Array.isArray(v) ? v : [];
              
              let tasksObj = {};
              if (data.coordinator_tasks) {
                try {
                  const t = typeof data.coordinator_tasks === 'string' && data.coordinator_tasks.trim().startsWith('{')
                    ? JSON.parse(data.coordinator_tasks)
                    : data.coordinator_tasks;
                  tasksObj = typeof t === 'object' && t !== null ? t : {};
                } catch {
                  tasksObj = {};
                }
              }
              
              let idsArray = [];
              if (data.coordinator_ids) {
                try {
                  const i = typeof data.coordinator_ids === 'string' && data.coordinator_ids.trim().startsWith('[')
                    ? JSON.parse(data.coordinator_ids)
                    : data.coordinator_ids;
                  idsArray = Array.isArray(i) ? i : [];
                } catch {
                  idsArray = [];
                }
              }
              
              coordinators = namesArray.map((name, index) => {
                const userId = idsArray[index] || '';
                const taskStr = tasksObj[userId] || tasksObj[`coordinator_${index}`] || '';
                const tasks = taskStr ? taskStr.split(';').map(t => t.trim()).filter(t => t) : [''];
                const { honorific, name: cleanName } = extractHonorificAndName(name);
                return { name: cleanName, honorific, tasks: tasks.length > 0 ? tasks : [''] };
              });
              
              if (coordinators.length === 0) {
                coordinators = [{ name: '', honorific: '', tasks: [''] }];
              }
            } catch {
              coordinators = [{ name: '', honorific: '', tasks: [''] }];
            }
          }

          // Load project staff
          let projectStaffs = [{ name: '', honorific: '', tasks: [''] }];
          if (data.project_staff_names) {
            try {
              let namesArray = [];
              const v = typeof data.project_staff_names === 'string' && data.project_staff_names.trim().startsWith('[')
                ? JSON.parse(data.project_staff_names)
                : data.project_staff_names;
              namesArray = Array.isArray(v) ? v : [];
              
              let tasksObj = {};
              if (data.project_staff_tasks) {
                try {
                  const t = typeof data.project_staff_tasks === 'string' && data.project_staff_tasks.trim().startsWith('{')
                    ? JSON.parse(data.project_staff_tasks)
                    : data.project_staff_tasks;
                  tasksObj = typeof t === 'object' && t !== null ? t : {};
                } catch {
                  tasksObj = {};
                }
              }
              
              let idsArray = [];
              if (data.project_staff_ids) {
                try {
                  const i = typeof data.project_staff_ids === 'string' && data.project_staff_ids.trim().startsWith('[')
                    ? JSON.parse(data.project_staff_ids)
                    : data.project_staff_ids;
                  idsArray = Array.isArray(i) ? i : [];
                } catch {
                  idsArray = [];
                }
              }
              
              projectStaffs = namesArray.map((name, index) => {
                const userId = idsArray[index] || '';
                const taskStr = tasksObj[userId] || tasksObj[`staff_${index}`] || '';
                const tasks = taskStr ? taskStr.split(';').map(t => t.trim()).filter(t => t) : [''];
                const { honorific, name: cleanName } = extractHonorificAndName(name);
                return { name: cleanName, honorific, tasks: tasks.length > 0 ? tasks : [''] };
              });
              
              if (projectStaffs.length === 0) {
                projectStaffs = [{ name: '', honorific: '', tasks: [''] }];
              }
            } catch {
              projectStaffs = [{ name: '', honorific: '', tasks: [''] }];
            }
          }

          setTrackingNumber(data.tracking_number || '');
          setSignatoryPrefs({
            prepared_by_enabled: data.prepared_by_enabled !== undefined ? !!data.prepared_by_enabled : true,
            reviewed_by_enabled: data.reviewed_by_enabled !== undefined ? !!data.reviewed_by_enabled : true,
            ra_1_enabled: data.ra_1_enabled !== undefined ? !!data.ra_1_enabled : true,
            ra_2_enabled: data.ra_2_enabled !== undefined ? !!data.ra_2_enabled : true,
            approved_by_enabled: data.approved_by_enabled !== undefined ? !!data.approved_by_enabled : true
          });
          setFormData({
            title: data.title || '',
            location: data.location || '',
            startDate: formatDateForInput(data.start_date),
            endDate: formatDateForInput(data.end_date),
            startTime: formatTimeForInput(data.start_time),
            endTime: formatTimeForInput(data.end_time),
            requestType: mapRequestType(data.request_type) || '',
            eventType: mapEventType(data.event_type || data.initiative_type) || '',
            extensionAgenda: extensionAgenda,
            sdg: parseList(data.sdg_goals),
            officesInvolved: data.offices_involved || '',
            programsInvolved: data.programs_involved || '',
            partnerAgencies: data.partner_agencies || '',
            beneficiaries: data.beneficiaries || '',
            selectedProjectLeader: projectLeaderId,
            projectLeader: projectLeaderName,
            projectLeaderHonorific: projectLeaderHonorific,
            projectLeaderTasks: data.project_leader_task ? (data.project_leader_task.split(';').map(t => t.trim()).filter(t => t).length > 0 ? data.project_leader_task.split(';').map(t => t.trim()).filter(t => t) : ['']) : [''],
            assistantLeaders: assistantLeaders,
            coordinators: coordinators,
            projectStaffs: projectStaffs,
            rationale: data.rationale || '',
            objectives: data.objectives || '',
            expectedOutput: data.expected_output || '',
            strategiesMethods: data.strategies_methods || '',
            functionalRelationships: data.functional_relationships || '',
            totalCost: data.total_cost ? formatNumberWithCommas(data.total_cost) : '',
            fundSource: fundSourceValue,
            financialPlanDetails: data.financial_plan_details || '',
            sustainabilityPlan: data.sustainability_plan || '',
            status: data.status || ''
          });
          
          // Track original fund source to detect changes
          setOriginalFundSource(fundSourceValue);
          setFundSourceOther(othersText);
          setFinancialPlanRows(financialPlanRowsData);
          setMeRows(meRowsData);
          setActiveTab('basic');
        } catch (err) {
          console.error('Error loading project:', err);
          showError('Failed to load project details');
        } finally {
          setLoading(false);
        }
      };
      
      loadProject();
    }
  }, [show, projectId, showError]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setFormData({
        title: '',
        location: '',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        requestType: '',
        eventType: '',
        extensionAgenda: '',
        sdg: [],
        officesInvolved: '',
        programsInvolved: '',
        partnerAgencies: '',
        beneficiaries: '',
        selectedProjectLeader: '',
        projectLeader: '',
        projectLeaderTasks: [''],
        assistantLeaders: [{ name: '', honorific: '', tasks: [''] }],
        coordinators: [{ name: '', honorific: '', tasks: [''] }],
        projectStaffs: [{ name: '', task: '' }],
        rationale: '',
        objectives: '',
        expectedOutput: '',
        strategiesMethods: '',
        functionalRelationships: '',
        totalCost: '',
        fundSource: '',
        financialPlanDetails: '',
        sustainabilityPlan: '',
        status: ''
      });
      setOriginalFundSource('');
      setFundSourceOther('');
      setFinancialPlanRows([
        { description: '', quantity: '', unit: '', price: '', total: '' },
        { description: '', quantity: '', unit: '', price: '', total: '' },
        { description: '', quantity: '', unit: '', price: '', total: '' },
        { description: '', quantity: '', unit: '', price: '', total: '' }
      ]);
      setMeRows([
        makeEmptyMeRow(), 
        makeEmptyMeRow(), 
        makeEmptyMeRow(), 
        makeEmptyMeRow(), 
        makeEmptyMeRow()
      ]);
      setSignatoryPrefs({
        prepared_by_enabled: true,
        reviewed_by_enabled: true,
        ra_1_enabled: true,
        ra_2_enabled: true,
        approved_by_enabled: true
      });
      setActiveTab('basic');
      setShowDeleteLibModal(false);
      setShowBudgetModal(false);
      setUpdatedProjectData(null);
    }
  }, [show]);

  const extensionAgendaOptions = [
    'BatStateU Inclusive Social Innovation for Regional Growth (BISIG) Program',
    'Livelihood and other Entrepreneurship related on Agri-Fisheries (LEAF)',
    'Environment and Natural Resources Conservation, Protection and Rehabilitation Program',
    'Smart Analytics and Engineering Innovation',
    'Adopt-a Municipality/Barangay/School/Social Development Thru BIDANI Implementation',
    'Community Outreach',
    'Technical- Vocational Education and Training (TVET) Program',
    'Technology Transfer and Adoption/Utilization Program',
    'Technical Assistance and Advisory Services Program',
    "Parents' Empowerment through Social Development (PESODEV)",
    'Gender and Development',
    'Disaster Risk Reduction and Management and Disaster Preparedness and Response/Climate Change Adaptation (DRRM and DPR/CCA)'
  ];

  const sdgOptions = [
    'No Poverty',
    'Zero Hunger',
    'Good Health and Well-Being',
    'Quality Education',
    'Gender Equality',
    'Clean Water and Sanitation',
    'Affordable and Clean Energy',
    'Decent Work and Economic Growth',
    'Industry, Innovation and Infrastructure',
    'Reduced Inequalities',
    'Sustainable Cities and Communities',
    'Responsible Consumption and Production',
    'Climate Action',
    'Life Below Water',
    'Life on Land',
    'Peace, Justice and Strong Institutions',
    'Partnerships for the Goals'
  ];

  const honorificOptions = [
    { value: '', label: 'None' },
    { value: 'Mr.', label: 'Mr.' },
    { value: 'Mrs.', label: 'Mrs.' },
    { value: 'Ms.', label: 'Ms.' },
    { value: 'Dr.', label: 'Dr.' },
    { value: 'Engr.', label: 'Engr.' },
    { value: 'Prof.', label: 'Prof.' },
    { value: 'Assoc. Prof.', label: 'Assoc. Prof.' },
    { value: 'Atty.', label: 'Atty.' },
    { value: 'Rev.', label: 'Rev.' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'sdg') {
      setFormData(prev => {
        const isCurrentlyChecked = prev.sdg.includes(value);
        const newSdg = isCurrentlyChecked
          ? prev.sdg.filter(item => item !== value)
          : [...prev.sdg, value];
        return {
          ...prev,
          sdg: newSdg
        };
      });
    } else if (name === 'totalCost') {
      const formattedValue = formatNumberWithCommas(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Handle Project Leader dropdown selection
  const handleProjectLeaderChange = (selectedOption) => {
    if (selectedOption) {
      setFormData(prev => ({
        ...prev,
        selectedProjectLeader: selectedOption.value,
        projectLeader: selectedOption.user.fullname
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedProjectLeader: '',
        projectLeader: ''
      }));
    }
  };

  const updateArrayField = (field, index, key, value) => {
    setFormData(prev => {
      const arr = [...prev[field]];
      if (key === 'name' && typeof value === 'object' && value !== null) {
        arr[index] = { ...arr[index], [key]: value.user.fullname };
      } else {
        arr[index] = { ...arr[index], [key]: value };
      }
      return { ...prev, [field]: arr };
    });
  };

  const addArrayRow = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...prev[field], { name: '', honorific: '', tasks: [''] }] }));
  };

  const removeArrayRow = (field, index) => {
    setFormData(prev => {
      const arr = prev[field].filter((_, i) => i !== index);
      return { ...prev, [field]: arr.length ? arr : [{ name: '', honorific: '', tasks: [''] }] };
    });
  };

  // Add/remove tasks for a specific user
  const addTask = (field, userIndex) => {
    setFormData(prev => {
      const arr = [...prev[field]];
      arr[userIndex] = { ...arr[userIndex], tasks: [...arr[userIndex].tasks, ''] };
      return { ...prev, [field]: arr };
    });
  };

  const removeTask = (field, userIndex, taskIndex) => {
    setFormData(prev => {
      const arr = [...prev[field]];
      if (arr[userIndex].tasks.length > 1) {
        arr[userIndex] = { ...arr[userIndex], tasks: arr[userIndex].tasks.filter((_, i) => i !== taskIndex) };
      }
      return { ...prev, [field]: arr };
    });
  };

  const updateTask = (field, userIndex, taskIndex, value) => {
    setFormData(prev => {
      const arr = [...prev[field]];
      const tasks = [...arr[userIndex].tasks];
      tasks[taskIndex] = value;
      arr[userIndex] = { ...arr[userIndex], tasks };
      return { ...prev, [field]: arr };
    });
  };

  // Add/remove tasks for project leader
  const addProjectLeaderTask = () => {
    setFormData(prev => ({ ...prev, projectLeaderTasks: [...prev.projectLeaderTasks, ''] }));
  };

  const removeProjectLeaderTask = (taskIndex) => {
    setFormData(prev => {
      if (prev.projectLeaderTasks.length > 1) {
        return { ...prev, projectLeaderTasks: prev.projectLeaderTasks.filter((_, i) => i !== taskIndex) };
      }
      return prev;
    });
  };

  const updateProjectLeaderTask = (taskIndex, value) => {
    setFormData(prev => {
      const tasks = [...prev.projectLeaderTasks];
      tasks[taskIndex] = value;
      return { ...prev, projectLeaderTasks: tasks };
    });
  };

  // Helper function to find a user option by name (from all users, not filtered)
  const findUserOptionByName = (name) => {
    if (!name) return null;
    // Try exact match first
    let user = users.find(u => u.fullname === name);
    // If no exact match, try case-insensitive match
    if (!user) {
      user = users.find(u => u.fullname.toLowerCase() === name.toLowerCase());
    }
    // If still no match, try matching without honorifics (in case name was loaded without honorific)
    if (!user) {
      user = users.find(u => {
        const uName = u.fullname.toLowerCase();
        const searchName = name.toLowerCase();
        // Check if names match after removing honorifics
        const uNameNoHonorific = uName.replace(/^(mr\.|mrs\.|ms\.|dr\.|engr\.|prof\.|assoc\. prof\.|atty\.|rev\.)\s+/i, '').trim();
        const searchNameNoHonorific = searchName.replace(/^(mr\.|mrs\.|ms\.|dr\.|engr\.|prof\.|assoc\. prof\.|atty\.|rev\.)\s+/i, '').trim();
        return uNameNoHonorific === searchNameNoHonorific || uName === searchNameNoHonorific || uNameNoHonorific === searchName;
      });
    }
    if (!user) return null;
    return {
      value: user.user_id,
      label: `${user.fullname} (${user.role})`,
      user: user
    };
  };

  // Get available users for dropdowns (excluding already selected ones)
  const getAvailableUsers = (excludeField, currentIndex = null) => {
    const selectedNames = new Set();
    
    if (formData.selectedProjectLeader) {
      const projectLeader = users.find(u => u.user_id === formData.selectedProjectLeader);
      if (projectLeader) selectedNames.add(projectLeader.fullname);
    }
    
    // Add assistant leaders, but exclude current index if editing that field
    formData.assistantLeaders.forEach((leader, index) => {
      if (leader.name && !(excludeField === 'assistantLeaders' && currentIndex === index)) {
        selectedNames.add(leader.name);
      }
    });
    
    // Add coordinators, but exclude current index if editing that field
    formData.coordinators.forEach((coord, index) => {
      if (coord.name && !(excludeField === 'coordinators' && currentIndex === index)) {
        selectedNames.add(coord.name);
      }
    });

    // Add project staff, but exclude current index if editing that field
    formData.projectStaffs.forEach((staff, index) => {
      if (staff.name && !(excludeField === 'projectStaffs' && currentIndex === index)) {
        selectedNames.add(staff.name);
      }
    });
    
    // Get current user's name if editing a specific field
    let currentUserName = null;
    if (excludeField === 'assistantLeaders' && currentIndex !== null && formData.assistantLeaders[currentIndex]?.name) {
      currentUserName = formData.assistantLeaders[currentIndex].name;
    } else if (excludeField === 'coordinators' && currentIndex !== null && formData.coordinators[currentIndex]?.name) {
      currentUserName = formData.coordinators[currentIndex].name;
    } else if (excludeField === 'projectStaffs' && currentIndex !== null && formData.projectStaffs[currentIndex]?.name) {
      currentUserName = formData.projectStaffs[currentIndex].name;
    }
    
    return users
      .filter(user => {
        // Always include the current user if editing that field
        if (currentUserName && user.fullname === currentUserName) {
          return true;
        }
        // Exclude other selected users
        return !selectedNames.has(user.fullname);
      })
      .map(user => ({
        value: user.user_id,
        label: `${user.fullname} (${user.role})`,
        user: user
      }));
  };

  // Handle form submission - show confirmation modal first
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check if fund source changed from STF/MDS to non-STF/MDS
    const wasStfOrMds = originalFundSource === 'STF' || originalFundSource === 'MDS';
    const isNowStfOrMds = formData.fundSource === 'STF' || formData.fundSource === 'MDS';
    
    if (wasStfOrMds && !isNowStfOrMds) {
      // Show delete LIB confirmation modal
      setShowDeleteLibModal(true);
    } else {
      // Show regular confirmation modal
      setShowConfirmModal(true);
    }
  };

  // Shared function to perform the actual update
  const performUpdate = async () => {
    // Prepare payload including Basic Info and Team fields
    const payload = {
      signatory_prefs: signatoryPrefs,
      tracking_number: trackingNumber || '',
      request_type: formData.requestType === 'client' ? 'Client Requested' : 'Department Initiative',
      event_type: formData.eventType,
      title: formData.title,
      location: formData.location,
      start_date: formData.startDate,
      end_date: formData.endDate,
      start_time: formData.startTime || null,
      end_time: formData.endTime || null,
      extension_agenda: formData.extensionAgenda ? [formData.extensionAgenda] : [],
      sdg_goals: formData.sdg,
      offices_involved: formData.officesInvolved,
      programs_involved: formData.programsInvolved,
      partner_agencies: formData.partnerAgencies,
      beneficiaries: formData.beneficiaries,
      rationale: formData.rationale,
      objectives: formData.objectives,
      expected_output: formData.expectedOutput,
      strategies_methods: formData.strategiesMethods,
      functional_relationships: formData.functionalRelationships,
      total_cost: removeCommas(formData.totalCost),
      fund_source: formData.fundSource === 'Others' 
        ? [`Others: ${fundSourceOther.trim()}`] 
        : (formData.fundSource ? [formData.fundSource] : []),
      financial_plan_details: formData.financialPlanDetails,
      monitoring_evaluation_table: { rows: meRows },
      sustainability_plan: formData.sustainabilityPlan,
      // Include status if Admin is editing
      ...(user?.role === 'Admin' && formData.status ? { status: formData.status } : {}),
      project_leader_id: formData.selectedProjectLeader,
      project_leader_name: (formData.projectLeaderHonorific ? `${formData.projectLeaderHonorific} ` : '') + formData.projectLeader + (formData.projectLeaderTasks.filter(t => t.trim()).length > 0 ? ` - ${formData.projectLeaderTasks.filter(t => t.trim()).join('; ')}` : ''),
      project_leader_honorific: formData.projectLeaderHonorific,
      project_leader_task: formData.projectLeaderTasks.filter(t => t.trim()).join('; '),
      assistant_project_leader_ids: formData.assistantLeaders
        .filter(leader => leader.name)
        .map(leader => {
          const user = users.find(u => u.fullname === leader.name);
          return user ? user.user_id : null;
        }),
      assistant_project_leader_names: formData.assistantLeaders
        .filter(leader => leader.name)
        .map(leader => {
          const tasksStr = leader.tasks.filter(t => t.trim()).join('; ');
          return (leader.honorific ? `${leader.honorific} ` : '') + leader.name + (tasksStr ? ` - ${tasksStr}` : '');
        }),
      assistant_project_leader_honorifics: formData.assistantLeaders
        .filter(leader => leader.name)
        .reduce((honorifics, leader, index) => {
          const user = users.find(u => u.fullname === leader.name);
          if (user && leader.honorific) {
            honorifics[user.user_id] = leader.honorific;
          } else if (leader.honorific) {
            honorifics[index] = leader.honorific;
          }
          return honorifics;
        }, {}),
      assistant_project_leader_tasks: formData.assistantLeaders
        .filter(leader => leader.name)
        .reduce((tasks, leader, index) => {
          const user = users.find(u => u.fullname === leader.name);
          const tasksStr = leader.tasks.filter(t => t.trim()).join('; ');
          if (tasksStr) {
            if (user) {
              tasks[user.user_id] = tasksStr;
            } else {
              // If no user found, use index as key to preserve task
              tasks[`assistant_${index}`] = tasksStr;
            }
          }
          return tasks;
        }, {}),
      coordinator_ids: [...formData.coordinators]
        .filter(coord => coord.name)
        .map(coord => {
          const user = users.find(u => u.fullname === coord.name);
          return user ? user.user_id : null;
        })
        .filter(id => id !== null),
      coordinator_names: [...formData.coordinators]
        .filter(coord => coord.name)
        .map(coord => {
          const tasksStr = coord.tasks.filter(t => t.trim()).join('; ');
          return coord.name + (tasksStr ? ` - ${tasksStr}` : '');
        }),
      coordinator_tasks: [...formData.coordinators]
        .filter(coord => coord.name)
        .reduce((tasks, coord, index) => {
          const user = users.find(u => u.fullname === coord.name);
          const tasksStr = coord.tasks.filter(t => t.trim()).join('; ');
          if (tasksStr) {
            if (user) {
              tasks[user.user_id] = tasksStr;
            } else {
              // If no user found, use index as key to preserve task
              tasks[`coordinator_${index}`] = tasksStr;
            }
          }
          return tasks;
        }, {}),
      project_staff_ids: formData.projectStaffs
        .filter(staff => staff.name)
        .map(staff => {
          const user = users.find(u => u.fullname === staff.name);
          return user ? user.user_id : null;
        })
        ,
      project_staff_names: formData.projectStaffs
        .filter(staff => staff.name)
        .map(staff => (staff.honorific ? `${staff.honorific} ` : '') + staff.name),
      project_staff_honorifics: formData.projectStaffs
        .filter(staff => staff.name)
        .reduce((honorifics, staff, index) => {
          const user = users.find(u => u.fullname === staff.name);
          if (user && staff.honorific) {
            honorifics[user.user_id] = staff.honorific;
          } else if (staff.honorific) {
            honorifics[index] = staff.honorific;
          }
          return honorifics;
        }, {}),
      project_staff_tasks: formData.projectStaffs
        .filter(staff => staff.name)
        .reduce((tasks, staff, index) => {
          const user = users.find(u => u.fullname === staff.name);
          const tasksStr = staff.tasks.filter(t => t.trim()).join('; ');
          if (tasksStr) {
            if (user) {
              tasks[user.user_id] = tasksStr;
            } else {
              // If no user found, use index as key to preserve task
              tasks[`staff_${index}`] = tasksStr;
            }
          }
          return tasks;
        }, {})
    };

    // Log payload for debugging (especially status)
    if (user?.role === 'Admin' && formData.status) {
      console.log('🔍 Updating project with status:', {
        projectId,
        status: formData.status,
        userRole: user.role
      });
    }

    await axios.put(`/projects/${projectId}`, payload);
    
    showSuccess('Project updated successfully!');
    
    // If STF or MDS is selected, fetch updated project data and open LIB modal
    if (formData.fundSource === 'STF' || formData.fundSource === 'MDS') {
      try {
        const { data: updatedData } = await axios.get(`/projects/${projectId}`);
        setUpdatedProjectData(updatedData);
        setShowBudgetModal(true);
        // Don't close the edit modal yet - let user edit LIB first
      } catch (error) {
        console.error('Error fetching updated project data:', error);
        // Show error but still close modal since project was updated successfully
        showError('Project updated successfully, but failed to load budget form. You can edit the budget later from the project view.');
        onHide();
        if (onSuccess) onSuccess();
      }
    } else {
      // For non-STF/MDS, close the modal normally
      onHide();
      if (onSuccess) onSuccess();
    }
  };

  // Handle delete LIB and then update
  const handleDeleteLibAndUpdate = async () => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      setShowDeleteLibModal(false);
      
      // Delete the budget first
      try {
        await apiClient.delete(`/budgets/by-project/${projectId}`);
        console.log('Budget deleted successfully');
      } catch (deleteError) {
        console.error('Error deleting budget:', deleteError);
        // Continue with update even if delete fails (budget might not exist)
      }
      
      // Proceed with the update
      await performUpdate();
      
    } catch (error) {
      console.error('Error in delete LIB and update:', error);
      
      // Handle timeout errors
      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout') || error?.message === 'Timeout') {
        showError('Request timed out. Please check your connection and try again.');
      } else if (error?.response?.status === 404) {
        showError('Project not found. Please refresh the page.');
      } else if (error?.response?.status >= 500) {
        showError('Server error. Please try again later.');
      } else if (error?.response?.data?.error) {
        showError(error.response.data.error);
      } else if (error?.message) {
        showError(error.message);
      } else {
        showError('Failed to update project. Please try again.');
      }
      setSubmitting(false);
    }
  };

  // Handle actual update after confirmation
  const handleConfirmSubmit = async () => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      setShowConfirmModal(false);

      await performUpdate();
      
    } catch (error) {
      console.error('Error updating project:', error);
      
      // Handle timeout errors
      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout') || error?.message === 'Timeout') {
        showError('Request timed out. Please check your connection and try again.');
        setSubmitting(false);
        return;
      }
      
      // Handle network errors
      if (!error?.response) {
        showError('Network error. Please check your connection and try again.');
        setSubmitting(false);
        return;
      }
      
      const status = error?.response?.status;
      const details = error?.response?.data?.details || error?.response?.data?.error;
      const msg = details || (status ? `Request failed (${status}). See console for details.` : 'Failed to update project. Please try again.');
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const isRa2Applicable = (() => {
    const fs = String(formData.fundSource || '');
    return (
      fs === 'STF' ||
      fs === 'MDS' ||
      fs.includes('STF') ||
      fs.includes('MDS') ||
      fs.includes('Special Trust Fund') ||
      fs.includes('Miscellaneous and Other Sources')
    );
  })();

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered enforceFocus={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-pencil-square me-2"></i>
            Edit Project Proposal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
          {loading ? (
            <SkeletonForm fields={8} />
          ) : (
            <Form onSubmit={handleSubmit}>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3"
              >
                <Tab eventKey="basic" title={<><i className="bi bi-info-circle me-1"></i> Basic Info</>}>
                  {/* Tracking Number - always at the very top */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Tracking Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      style={{ border: '1px solid #ced4da', padding: '10px' }}
                    />
                    <Form.Text className="text-muted">
                      This value will appear in the footer as the Tracking No. for this Project Proposal.
                    </Form.Text>
                  </Form.Group>
                  <hr className="my-3" style={{ borderTop: '1px solid #dee2e6' }} />
                  
                  {/* Status field - Admin only */}
                  {user?.role === 'Admin' && (
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Project Status:</strong></Form.Label>
                      <Form.Select
                        value={formData.status}
                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Reviewed by GAD">Reviewed by GAD</option>
                        <option value="Approved by Extension Head">Approved by Extension Head</option>
                        <option value="Rejected by Extension Head">Rejected by Extension Head</option>
                        <option value="Approved by VC-RDES">Approved by VC-RDES</option>
                        <option value="Rejected by VC-RDES">Rejected by VC-RDES</option>
                        <option value="Approved by VC-AF">Approved by VC-AF</option>
                        <option value="Rejected by VC-AF">Rejected by VC-AF</option>
                        <option value="Approved by Chancellor">Approved by Chancellor</option>
                        <option value="Rejected by Chancellor">Rejected by Chancellor</option>
                        <option value="On-Going">On-Going</option>
                        <option value="Completed">Completed</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Only Admin can change project status
                      </Form.Text>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label><strong>Initiative Type:</strong></Form.Label>
                    <Form.Check
                      type="radio"
                      id="request_type-client"
                      name="requestType"
                      value="client"
                      checked={formData.requestType === 'client'}
                      onChange={handleInputChange}
                      onClick={handleInputChange}
                      label="Extension Service Program/Project/Activity is requested by clients."
                    />
                    <Form.Check
                      type="radio"
                      id="request_type-department"
                      name="requestType"
                      value="department"
                      checked={formData.requestType === 'department'}
                      onChange={handleInputChange}
                      onClick={handleInputChange}
                      label="Extension Service Program/Project/Activity is Department's initiative."
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label><strong>Event Type:</strong></Form.Label>
                    <Row>
                      <Col md={4}>
                        <Form.Check
                          type="radio"
                          id="event_type-program"
                          name="eventType"
                          value="Program"
                          checked={formData.eventType === 'Program'}
                          onChange={handleInputChange}
                          onClick={handleInputChange}
                          label="Program"
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Check
                          type="radio"
                          id="event_type-project"
                          name="eventType"
                          value="Project"
                          checked={formData.eventType === 'Project'}
                          onChange={handleInputChange}
                          onClick={handleInputChange}
                          label="Project"
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Check
                          type="radio"
                          id="event_type-activity"
                          name="eventType"
                          value="Activity"
                          checked={formData.eventType === 'Activity'}
                          onChange={handleInputChange}
                          onClick={handleInputChange}
                          label="Activity"
                        />
                      </Col>
                    </Row>
                  </Form.Group>

                  <hr />

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: '#d32f2f' }}><strong>Title:</strong></Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          placeholder="Enter project title"
                          maxLength={500}
                          style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', whiteSpace: 'pre-wrap' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: '#d32f2f' }}><strong>Location:</strong></Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          placeholder="Enter project location"
                          maxLength={500}
                          style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', whiteSpace: 'pre-wrap' }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: '#d32f2f' }}><strong>Start Date:</strong></Form.Label>
                        <Form.Control
                          type="date"
                          name="startDate"
                          value={formData.startDate}
                          onChange={handleInputChange}
                          style={{ border: '1px solid #888', fontSize: '1em' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: '#d32f2f' }}><strong>End Date:</strong></Form.Label>
                        <Form.Control
                          type="date"
                          name="endDate"
                          value={formData.endDate}
                          onChange={handleInputChange}
                          style={{ border: '1px solid #888', fontSize: '1em' }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: '#d32f2f' }}><strong>Start Time (Optional):</strong></Form.Label>
                        <Form.Control
                          type="time"
                          name="startTime"
                          value={formData.startTime}
                          onChange={handleInputChange}
                          disabled={!(formData.eventType === 'Project' || formData.eventType === 'Activity')}
                          style={{ border: '1px solid #888', fontSize: '1em' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: '#d32f2f' }}><strong>End Time (Optional):</strong></Form.Label>
                        <Form.Control
                          type="time"
                          name="endTime"
                          value={formData.endTime}
                          onChange={handleInputChange}
                          disabled={!(formData.eventType === 'Project' || formData.eventType === 'Activity')}
                          style={{ border: '1px solid #888', fontSize: '1em' }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr />
                  <h5 style={{ color: '#d32f2f' }}>Extension Service Agenda</h5>
                  <Form.Group className="mb-3">
                    {extensionAgendaOptions.map((option, index) => (
                      <Form.Check
                        key={index}
                        type="radio"
                        label={option}
                        name="extensionAgenda"
                        value={option}
                        checked={formData.extensionAgenda === option}
                        onChange={handleInputChange}
                        onClick={handleInputChange}
                        className="mb-2"
                      />
                    ))}
                  </Form.Group>

                  <hr />
                  <h5 style={{ color: '#d32f2f' }}>Sustainable Development Goals (SDG)</h5>
                  <Form.Group className="mb-3">
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        {sdgOptions.slice(0, 9).map((option, index) => (
                          <Form.Check
                            key={index}
                            type="checkbox"
                            label={option}
                            name="sdg"
                            value={option}
                            checked={formData.sdg.includes(option)}
                            onChange={handleInputChange}
                            onClick={handleInputChange}
                            className="mb-2"
                          />
                        ))}
                      </div>
                      <div style={{ flex: 1 }}>
                        {sdgOptions.slice(9).map((option, index) => (
                          <Form.Check
                            key={index}
                            type="checkbox"
                            label={option}
                            name="sdg"
                            value={option}
                            checked={formData.sdg.includes(option)}
                            onChange={handleInputChange}
                            onClick={handleInputChange}
                            className="mb-2"
                          />
                        ))}
                      </div>
                    </div>
                  </Form.Group>

                  <hr />
                  <h5 style={{ color: '#d32f2f' }}>Offices/Colleges/Organizations Involved</h5>
                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="officesInvolved"
                      value={formData.officesInvolved}
                      onChange={handleInputChange}
                      placeholder="Enter offices/colleges/organizations involved"
                      maxLength={500}
                      style={{ border: '1px solid #888', fontSize: '1em' }}
                    />
                  </Form.Group>

                  <hr />
                  <h5 style={{ color: '#d32f2f' }}>Programs Involved</h5>
                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="programsInvolved"
                      value={formData.programsInvolved}
                      onChange={handleInputChange}
                      placeholder="Enter programs involved"
                      maxLength={500}
                      style={{ border: '1px solid #888', fontSize: '1em' }}
                    />
                  </Form.Group>

                  <hr />
                  <h5 style={{ color: '#d32f2f' }}>Partner Agencies</h5>
                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="partnerAgencies"
                      value={formData.partnerAgencies}
                      onChange={handleInputChange}
                      placeholder="Enter partner agencies"
                      maxLength={500}
                      style={{ border: '1px solid #888', fontSize: '1em' }}
                    />
                  </Form.Group>

                  <hr />
                  <h5 style={{ color: '#d32f2f' }}>Beneficiaries</h5>
                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="beneficiaries"
                      value={formData.beneficiaries}
                      onChange={handleInputChange}
                      placeholder="Enter beneficiaries"
                      maxLength={500}
                      style={{ border: '1px solid #888', fontSize: '1em' }}
                    />
                  </Form.Group>
                </Tab>

                <Tab eventKey="team" title={<><i className="bi bi-people me-1"></i> Team</>}>
                  <h4 style={{ color: '#d32f2f' }}>Project Leadership</h4>
                  {/* Project Leader (single) */}
                  <Row>
                    <Col md={4}>
                      {loading ? (
                        <SkeletonSelect />
                      ) : (
                        <SelectField
                          label="Project Leader"
                          name="selectedProjectLeader"
                          value={formData.selectedProjectLeader ? findUserOptionByName(formData.projectLeader) || getAvailableUsers('projectLeader').find(option => option.value === formData.selectedProjectLeader) : null}
                          onChange={handleProjectLeaderChange}
                          options={getAvailableUsers('projectLeader')}
                          placeholder="Select Project Leader"
                          isDisabled={loading}
                          labelColor="#d32f2f"
                        />
                      )}
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: '#d32f2f' }}><strong>Honorific:</strong></Form.Label>
                        <Form.Select
                          name="projectLeaderHonorific"
                          value={formData.projectLeaderHonorific}
                          onChange={handleInputChange}
                          style={{ border: '1px solid #888', fontSize: '1em' }}
                        >
                          {honorificOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <Form.Label className="mb-0"><strong>Tasks:</strong></Form.Label>
                          <Button
                            variant="link"
                            className="p-0"
                            style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                            title="Add task"
                            onClick={addProjectLeaderTask}
                          >
                            <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                          </Button>
                        </div>
                        {(formData.projectLeaderTasks || ['']).map((task, taskIdx) => (
                          <div key={`pl-task-${taskIdx}`} className="mb-2 d-flex align-items-start">
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={task}
                              onChange={(e) => updateProjectLeaderTask(taskIdx, e.target.value)}
                              placeholder={`Task ${taskIdx + 1}`}
                              maxLength={2000}
                              style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', flex: 1 }}
                            />
                            {formData.projectLeaderTasks.length > 1 && (
                              <Button
                                variant="link"
                                className="p-0 ms-2"
                                style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                                title="Remove task"
                                onClick={() => removeProjectLeaderTask(taskIdx)}
                              >
                                <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                              </Button>
                            )}
                          </div>
                        ))}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Assistant Project Leaders (multiple) - Full Width */}
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-2">
                        <Form.Label className="mb-0">
                          <strong>Assistant Project Leaders</strong>
                          <Button
                            variant="link"
                            className="p-0 ms-2"
                            style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                            title="Add assistant project leader"
                            aria-label="Add assistant project leader"
                            onClick={() => addArrayRow('assistantLeaders')}
                          >
                            <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                          </Button>
                          {formData.assistantLeaders.length > 1 && (
                            <Button
                              variant="link"
                              className="p-0 ms-1"
                              style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                              title="Remove last assistant project leader"
                              aria-label="Remove assistant project leader"
                              onClick={() => removeArrayRow('assistantLeaders', formData.assistantLeaders.length - 1)}
                            >
                              <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                            </Button>
                          )}
                        </Form.Label>
                      </Form.Group>
                      {formData.assistantLeaders.map((row, idx) => (
                        <Row key={`apl-row-${idx}`} className="mb-2 align-items-start">
                          <Col md={4}>
                            {loading ? (
                              <SkeletonSelect />
                            ) : (
                              <SelectField
                                value={row.name ? findUserOptionByName(row.name) || getAvailableUsers('assistantLeaders', idx).find(option => option.user.fullname === row.name) : null}
                                onChange={(selectedOption) => updateArrayField('assistantLeaders', idx, 'name', selectedOption)}
                                options={getAvailableUsers('assistantLeaders', idx)}
                                placeholder="Select Assistant Project Leader"
                                isDisabled={loading}
                                labelColor=""
                              />
                            )}
                          </Col>
                          <Col md={2}>
                            <Form.Select
                              value={row.honorific || ''}
                              onChange={(e) => updateArrayField('assistantLeaders', idx, 'honorific', e.target.value)}
                              style={{ border: '1px solid #888', fontSize: '1em' }}
                            >
                              {honorificOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={6} className="d-flex flex-column">
                            <div className="d-flex align-items-center justify-content-between mb-2" style={{ marginTop: 0, minHeight: '38px', alignItems: 'flex-start', paddingTop: '6px' }}>
                              <Form.Label className="mb-0" style={{ lineHeight: '1.5' }}><strong>Tasks:</strong></Form.Label>
                              <Button
                                variant="link"
                                className="p-0"
                                style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                                title="Add task"
                                onClick={() => addTask('assistantLeaders', idx)}
                              >
                                <i className="bi bi-plus-circle-fill" style={{ fontSize: 16 }}></i>
                              </Button>
                            </div>
                            <div style={{ flex: 1 }}>
                            {(row.tasks || ['']).map((task, taskIdx) => (
                              <div key={`apl-${idx}-task-${taskIdx}`} className="mb-2 d-flex align-items-start">
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  value={task}
                                  onChange={(e) => updateTask('assistantLeaders', idx, taskIdx, e.target.value)}
                                  placeholder={`Task ${taskIdx + 1}`}
                                  maxLength={2000}
                                  style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', flex: 1 }}
                                />
                                {(row.tasks || ['']).length > 1 && (
                                  <Button
                                    variant="link"
                                    className="p-0 ms-2"
                                    style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                                    title="Remove task"
                                    onClick={() => removeTask('assistantLeaders', idx, taskIdx)}
                                  >
                                    <i className="bi bi-dash-circle-fill" style={{ fontSize: 16 }}></i>
                                  </Button>
                                )}
                              </div>
                            ))}
                            </div>
                          </Col>
                        </Row>
                      ))}
                    </Col>
                  </Row>

                  {/* Project Staff (multiple) - Same structure as Assistant Project Leaders */}
                  <Row className="mt-3">
                    <Col md={12}>
                      <Form.Group className="mb-2">
                        <Form.Label className="mb-0">
                          <strong>Project Staff</strong>
                          <Button
                            variant="link"
                            className="p-0 ms-2"
                            style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                            title="Add project staff"
                            aria-label="Add project staff"
                            onClick={() => addArrayRow('projectStaffs')}
                          >
                            <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                          </Button>
                          {formData.projectStaffs.length > 1 && (
                            <Button
                              variant="link"
                              className="p-0 ms-1"
                              style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                              title="Remove last project staff"
                              aria-label="Remove project staff"
                              onClick={() => removeArrayRow('projectStaffs', formData.projectStaffs.length - 1)}
                            >
                              <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                            </Button>
                          )}
                        </Form.Label>
                      </Form.Group>
                      {formData.projectStaffs.map((row, idx) => (
                        <Row key={`staff-row-${idx}`} className="mb-2 align-items-start">
                          <Col md={4}>
                            {loading ? (
                              <SkeletonSelect />
                            ) : (
                              <SelectField
                                value={row.name ? findUserOptionByName(row.name) || getAvailableUsers('projectStaffs', idx).find(option => option.user.fullname === row.name) : null}
                                onChange={(selectedOption) => updateArrayField('projectStaffs', idx, 'name', selectedOption)}
                                options={getAvailableUsers('projectStaffs', idx)}
                                placeholder="Select Project Staff"
                                isDisabled={loading}
                                labelColor=""
                              />
                            )}
                          </Col>
                          <Col md={2}>
                            <Form.Select
                              value={row.honorific || ''}
                              onChange={(e) => updateArrayField('projectStaffs', idx, 'honorific', e.target.value)}
                              style={{ border: '1px solid #888', fontSize: '1em' }}
                            >
                              {honorificOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={6} className="d-flex flex-column">
                            <div className="d-flex align-items-center justify-content-between mb-2" style={{ marginTop: 0, minHeight: '38px', alignItems: 'flex-start', paddingTop: '6px' }}>
                              <Form.Label className="mb-0" style={{ lineHeight: '1.5' }}><strong>Tasks:</strong></Form.Label>
                              <Button
                                variant="link"
                                className="p-0"
                                style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                                title="Add task"
                                onClick={() => addTask('projectStaffs', idx)}
                              >
                                <i className="bi bi-plus-circle-fill" style={{ fontSize: 16 }}></i>
                              </Button>
                            </div>
                            <div style={{ flex: 1 }}>
                            {(row.tasks || ['']).map((task, taskIdx) => (
                              <div key={`staff-${idx}-task-${taskIdx}`} className="mb-2 d-flex align-items-start">
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  value={task}
                                  onChange={(e) => updateTask('projectStaffs', idx, taskIdx, e.target.value)}
                                  placeholder={`Task ${taskIdx + 1}`}
                                  maxLength={2000}
                                  style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', flex: 1 }}
                                />
                                {(row.tasks || ['']).length > 1 && (
                                  <Button
                                    variant="link"
                                    className="p-0 ms-2"
                                    style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                                    title="Remove task"
                                    onClick={() => removeTask('projectStaffs', idx, taskIdx)}
                                  >
                                    <i className="bi bi-dash-circle-fill" style={{ fontSize: 16 }}></i>
                                  </Button>
                                )}
                              </div>
                            ))}
                            </div>
                          </Col>
                        </Row>
                      ))}
                    </Col>
                  </Row>

                  {/* Coordinators (multiple) - Moved to bottom */}
                  <Row className="mt-3">
                    <Col md={12}>
                      <Form.Group className="mb-2 d-flex align-items-center justify-content-between">
                        <Form.Label className="mb-0"><strong>Coordinator</strong></Form.Label>
                        <div>
                          <Button
                            variant="link"
                            className="p-0 ms-2"
                            style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                            title="Add coordinator"
                            aria-label="Add coordinator"
                            onClick={() => addArrayRow('coordinators')}
                          >
                            <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                          </Button>
                          {formData.coordinators.length > 1 && (
                            <Button
                              variant="link"
                              className="p-0 ms-1"
                              style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                              title="Remove last coordinator"
                              aria-label="Remove coordinator"
                              onClick={() => removeArrayRow('coordinators', formData.coordinators.length - 1)}
                            >
                              <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                            </Button>
                          )}
                        </div>
                      </Form.Group>
                      {formData.coordinators.map((row, idx) => (
                        <Row key={`coord-row-${idx}`} className="mb-2 align-items-start">
                          <Col md={6}>
                            {loading ? (
                              <SkeletonSelect />
                            ) : (
                              <SelectField
                                value={row.name ? findUserOptionByName(row.name) || getAvailableUsers('coordinators', idx).find(option => option.user.fullname === row.name) : null}
                                onChange={(selectedOption) => updateArrayField('coordinators', idx, 'name', selectedOption)}
                                options={getAvailableUsers('coordinators', idx)}
                                placeholder="Select Coordinator"
                                isDisabled={loading}
                                labelColor=""
                              />
                            )}
                          </Col>
                          <Col md={6} className="d-flex flex-column">
                            <div className="d-flex align-items-center justify-content-between mb-2" style={{ marginTop: 0, minHeight: '38px', alignItems: 'flex-start', paddingTop: '6px' }}>
                              <Form.Label className="mb-0" style={{ lineHeight: '1.5' }}><strong>Tasks:</strong></Form.Label>
                              <Button
                                variant="link"
                                className="p-0"
                                style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                                title="Add task"
                                onClick={() => addTask('coordinators', idx)}
                              >
                                <i className="bi bi-plus-circle-fill" style={{ fontSize: 16 }}></i>
                              </Button>
                            </div>
                            <div style={{ flex: 1 }}>
                            {(row.tasks || ['']).map((task, taskIdx) => (
                              <div key={`coord-${idx}-task-${taskIdx}`} className="mb-2 d-flex align-items-start">
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  value={task}
                                  onChange={(e) => updateTask('coordinators', idx, taskIdx, e.target.value)}
                                  placeholder={`Task ${taskIdx + 1}`}
                                  maxLength={2000}
                                  style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', flex: 1 }}
                                />
                                {(row.tasks || ['']).length > 1 && (
                                  <Button
                                    variant="link"
                                    className="p-0 ms-2"
                                    style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                                    title="Remove task"
                                    onClick={() => removeTask('coordinators', idx, taskIdx)}
                                  >
                                    <i className="bi bi-dash-circle-fill" style={{ fontSize: 16 }}></i>
                                  </Button>
                                )}
                              </div>
                            ))}
                            </div>
                          </Col>
                        </Row>
                      ))}
                    </Col>
                  </Row>
                </Tab>
                <Tab eventKey="content" title={<><i className="bi bi-file-text me-1"></i> Content</>}>
                  <h5 style={{ color: '#d32f2f' }}>Rationale</h5>
                  <Form.Group className="mb-3">
                    {loading ? (
                      <SkeletonTextarea rows={3} />
                    ) : (
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="rationale"
                        value={formData.rationale}
                        onChange={handleInputChange}
                        placeholder="Enter project rationale"
                        style={{ border: '1px solid #888', fontSize: '1em' }}
                      />
                    )}
                  </Form.Group>

                  <hr />
                  <h5 style={{ color: '#d32f2f' }}>Objectives</h5>
                  <Form.Group className="mb-3">
                    {loading ? (
                      <SkeletonTextarea rows={3} />
                    ) : (
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="objectives"
                        value={formData.objectives}
                        onChange={handleInputChange}
                        placeholder="Enter project objectives"
                        style={{ border: '1px solid #888', fontSize: '1em' }}
                      />
                    )}
                  </Form.Group>

                  <hr />
                  <h5 style={{ color: '#d32f2f' }}>Program/Project Expected Output</h5>
                  <Form.Group className="mb-3">
                    {loading ? (
                      <SkeletonTextarea rows={3} />
                    ) : (
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="expectedOutput"
                        value={formData.expectedOutput}
                        onChange={handleInputChange}
                        placeholder="Enter expected output"
                        style={{ border: '1px solid #888', fontSize: '1em' }}
                      />
                    )}
                  </Form.Group>

                  <hr />
                  <h5 style={{ color: '#d32f2f' }}>Description, Strategies and Methods (Activities / Schedule)</h5>
                  <Form.Group className="mb-3">
                    {loading ? (
                      <SkeletonTextarea rows={3} />
                    ) : (
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="strategiesMethods"
                        value={formData.strategiesMethods}
                        onChange={handleInputChange}
                        placeholder="Enter strategies and methods"
                        style={{ border: '1px solid #888', fontSize: '1em' }}
                      />
                    )}
                  </Form.Group>

                  <hr />
                  <h5 style={{ color: '#d32f2f' }}>Functional Relationships with the Partner Agencies (Duties / Tasks of the Partner Agencies)</h5>
                  <Form.Group className="mb-3">
                    {loading ? (
                      <SkeletonTextarea rows={3} />
                    ) : (
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="functionalRelationships"
                        value={formData.functionalRelationships}
                        onChange={handleInputChange}
                        placeholder="Enter functional relationships details"
                        style={{ border: '1px solid #888', fontSize: '1em' }}
                      />
                    )}
                  </Form.Group>
                </Tab>
                <Tab eventKey="financial" title={<><i className="bi bi-cash-stack me-1"></i> Financial</>}>
                  <h5 style={{ color: '#d32f2f' }}>Total Cost</h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: '#d32f2f' }}><strong>Total Cost:</strong></Form.Label>
                        {loading ? (
                          <SkeletonSelect />
                        ) : (
                          <div className="input-group">
                            <span className="input-group-text">₱</span>
                            <Form.Control
                              type="text"
                              name="totalCost"
                              value={formData.totalCost}
                              onChange={handleInputChange}
                              placeholder="Enter total cost (e.g., 1,000,000.50)"
                              maxLength={50}
                              style={{ border: '1px solid #888', fontSize: '1em' }}
                            />
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr />
                  <h5 style={{ color: '#d32f2f' }}>Source of Fund</h5>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#d32f2f' }}><strong>Source of fund:</strong></Form.Label>
                    {loading ? (
                      <SkeletonSelect />
                    ) : (
                      <>
                        <div className="mb-2">
                          {['STF', 'MDS', 'Others'].map((fs) => (
                            <Form.Check
                              key={fs}
                              inline
                              type="radio"
                              label={fs === 'Others' ? 'Others, (Please specify)' : fs}
                              name="fundSource"
                              value={fs}
                              checked={formData.fundSource === fs}
                              onChange={handleInputChange}
                              onClick={handleInputChange}
                            />
                          ))}
                        </div>
                        {formData.fundSource === 'Others' && (
                          <Form.Control
                            placeholder="Specify other source"
                            value={fundSourceOther}
                            onChange={(e) => setFundSourceOther(e.target.value)}
                            maxLength={255}
                            style={{ border: '1px solid #888', fontSize: '1em' }}
                          />
                        )}
                      </>
                    )}
                  </Form.Group>

                  <hr />
                  <h5 style={{ color: '#d32f2f' }}>Financial Plan</h5>
                  <Form.Group className="mb-3">
                    {loading ? (
                      <SkeletonForm fields={3} />
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div></div>
                          <div className="d-flex gap-2">
                            <Button variant="link" className="p-0" style={{ color: '#0d6efd' }} onClick={addFinancialPlanRow} title="Add row">
                              <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                            </Button>
                            <Button variant="link" className="p-0" style={{ color: '#d32f2f' }} onClick={removeFinancialPlanRow} title="Remove last row" disabled={financialPlanRows.length <= 4}>
                              <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                            </Button>
                          </div>
                        </div>
                        <div style={{ width: '100%', overflowX: 'auto', border: '1px solid #dee2e6' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', margin: 0 }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f8f9fa' }}>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'left', width: '50%' }}>Item Description</th>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '10%' }}>Quantity</th>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '10%' }}>Unit</th>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'right', width: '15%' }}>Price (Php)</th>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'right', width: '15%' }}>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {financialPlanRows.map((row, idx) => (
                                <tr key={`fp-row-${idx}`}>
                                  <td style={{ border: '1px solid #000', padding: '6px' }}>
                                    <input 
                                      value={row.description} 
                                      onChange={(e) => updateFinancialRow(idx, 'description', e.target.value)} 
                                      style={{ width: '100%', border: 'none', outline: 'none' }} 
                                    />
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                    <input 
                                      value={row.quantity} 
                                      onChange={(e) => updateFinancialRow(idx, 'quantity', e.target.value)} 
                                      style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'center' }} 
                                    />
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                    <input 
                                      value={row.unit} 
                                      onChange={(e) => updateFinancialRow(idx, 'unit', e.target.value)} 
                                      style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'center' }} 
                                    />
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'right' }}>
                                    <input 
                                      value={row.price} 
                                      onChange={(e) => updateFinancialRow(idx, 'price', e.target.value)} 
                                      inputMode="decimal" 
                                      pattern="[0-9]*" 
                                      style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'right' }} 
                                    />
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'right' }}>
                                    <input 
                                      value={row.total} 
                                      onChange={(e) => updateFinancialRow(idx, 'total', e.target.value)} 
                                      inputMode="decimal" 
                                      pattern="[0-9]*" 
                                      style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'right' }} 
                                    />
                                  </td>
                                </tr>
                              ))}
                              <tr>
                                <td colSpan={4} style={{ border: '1px solid #000', padding: '6px', textAlign: 'right', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>TOTAL</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'right', fontWeight: 'bold' }}>
                                  {(() => {
                                    const g = getFinancialPlanGrandTotal();
                                    return g ? g.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
                                  })()}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </Form.Group>
                </Tab>
                <Tab eventKey="monitoring" title={<><i className="bi bi-bar-chart me-1"></i> Monitoring</>}>
                  <h5 style={{ color: '#d32f2f' }}>Monitoring and Evaluation Mechanics / Plan</h5>
                  <Form.Group className="mb-3">
                    {loading ? (
                      <SkeletonForm fields={3} />
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div></div>
                          <div className="d-flex gap-2">
                            <Button variant="link" className="p-0" style={{ color: '#0d6efd' }} onClick={addMeRow} title="Add row">
                              <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                            </Button>
                            <Button variant="link" className="p-0" style={{ color: '#d32f2f' }} onClick={removeMeRow} title="Remove last row" disabled={meRows.length <= 5}>
                              <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                            </Button>
                          </div>
                        </div>
                        <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #dee2e6', width: '100%' }}>
                          <table style={{ width: '100%', marginBottom: 0, borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: '1600px' }}>
                            <thead style={{ backgroundColor: '#e3f2fd', position: 'sticky', top: 0, zIndex: 1 }}>
                              <tr>
                                <th style={{ width: '15%', fontSize: '0.9rem', padding: '12px', border: '1px solid #dee2e6' }}>Objectives</th>
                                <th style={{ width: '20%', fontSize: '0.9rem', padding: '12px', border: '1px solid #dee2e6' }}>Performance Indicators</th>
                                <th style={{ width: '18%', fontSize: '0.9rem', padding: '12px', border: '1px solid #dee2e6' }}>Baseline Data</th>
                                <th style={{ width: '18%', fontSize: '0.9rem', padding: '12px', border: '1px solid #dee2e6' }}>Performance Target</th>
                                <th style={{ width: '18%', fontSize: '0.9rem', padding: '12px', border: '1px solid #dee2e6' }}>Data Source</th>
                                <th style={{ width: '18%', fontSize: '0.9rem', padding: '12px', border: '1px solid #dee2e6' }}>Collection Method</th>
                                <th style={{ width: '18%', fontSize: '0.9rem', padding: '12px', border: '1px solid #dee2e6' }}>Frequency of Data Collection</th>
                                <th style={{ width: '25%', fontSize: '0.9rem', padding: '12px', border: '1px solid #dee2e6' }}>Office/Persons Responsible</th>
                              </tr>
                            </thead>
                            <tbody>
                              {meRows.map((row, idx) => (
                                <tr key={`me-row-${idx}`} style={{ verticalAlign: 'top' }}>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <textarea 
                                      rows={3} 
                                      value={row.objectives} 
                                      onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, objectives: e.target.value } : r))} 
                                      style={{ fontSize: '0.9rem', resize: 'vertical', minHeight: '60px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '8px', margin: '0', outline: 'none' }} 
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <textarea 
                                      rows={3} 
                                      value={row.performance_indicators} 
                                      onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, performance_indicators: e.target.value } : r))} 
                                      style={{ fontSize: '0.9rem', resize: 'vertical', minHeight: '60px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '8px', margin: '0', outline: 'none' }} 
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <textarea 
                                      rows={3} 
                                      value={row.baseline_data} 
                                      onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, baseline_data: e.target.value } : r))} 
                                      style={{ fontSize: '0.9rem', resize: 'vertical', minHeight: '60px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '8px', margin: '0', outline: 'none' }} 
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <textarea 
                                      rows={3} 
                                      value={row.performance_target} 
                                      onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, performance_target: e.target.value } : r))} 
                                      style={{ fontSize: '0.9rem', resize: 'vertical', minHeight: '60px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '8px', margin: '0', outline: 'none' }} 
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <textarea 
                                      rows={3} 
                                      value={row.data_source} 
                                      onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, data_source: e.target.value } : r))} 
                                      style={{ fontSize: '0.9rem', resize: 'vertical', minHeight: '60px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '8px', margin: '0', outline: 'none' }} 
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <textarea 
                                      rows={3} 
                                      value={row.collection_method} 
                                      onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, collection_method: e.target.value } : r))} 
                                      style={{ fontSize: '0.9rem', resize: 'vertical', minHeight: '60px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '8px', margin: '0', outline: 'none' }} 
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <textarea 
                                      rows={3} 
                                      value={row.frequency} 
                                      onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, frequency: e.target.value } : r))} 
                                      style={{ fontSize: '0.9rem', resize: 'vertical', minHeight: '60px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '8px', margin: '0', outline: 'none' }} 
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <textarea 
                                      rows={3} 
                                      value={row.responsible} 
                                      onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, responsible: e.target.value } : r))} 
                                      style={{ fontSize: '0.9rem', resize: 'vertical', minHeight: '60px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '8px', margin: '0', outline: 'none' }} 
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </Form.Group>
                </Tab>
                <Tab eventKey="sustainability" title={<><i className="bi bi-leaf me-1"></i> Sustainability</>}>
                  <h5 style={{ color: '#d32f2f' }}>Sustainability Plan</h5>
                  <Form.Group className="mb-3">
                    {loading ? (
                      <SkeletonTextarea rows={10} />
                    ) : (
                      <Form.Control
                        as="textarea"
                        rows={10}
                        name="sustainabilityPlan"
                        value={formData.sustainabilityPlan}
                        onChange={handleInputChange}
                        placeholder="Enter sustainability plan"
                        style={{ border: '1px solid #888', fontSize: '1em' }}
                      />
                    )}
                  </Form.Group>
                </Tab>

                <Tab
                  eventKey="signatories"
                  title={
                    <span>
                      <i className="bi bi-pen me-2"></i>
                      Signatories
                    </span>
                  }
                >
                  <div className="pt-3">
                    <Alert variant="info" className="mb-3">
                      <i className="bi bi-info-circle me-2"></i>
                      Enable/disable which signatory sections will appear in the generated DOCX. Disabling a signatory will output no
                      name/designation/date/signature for that section.
                    </Alert>

                    {[
                      { key: 'prepared_by_enabled', id: 'pp-edit-signatory-prepared-by', label: 'Prepared by (Project Leader)' },
                      { key: 'reviewed_by_enabled', id: 'pp-edit-signatory-reviewed-by', label: 'Reviewed by (Extension Head)' },
                      { key: 'ra_1_enabled', id: 'pp-edit-signatory-ra1', label: 'Recommending Approval 1 (VC-RDES)' },
                      {
                        key: 'ra_2_enabled',
                        id: 'pp-edit-signatory-ra2',
                        label: 'Recommending Approval 2 (VC-AF - STF/MDS only)',
                        disabled: !isRa2Applicable
                      },
                      { key: 'approved_by_enabled', id: 'pp-edit-signatory-approved-by', label: 'Approved by (Chancellor)' }
                    ].map((opt) => {
                      const disabled = loading || submitting || !!opt.disabled;
                      return (
                        <div
                          key={opt.key}
                          role="checkbox"
                          aria-checked={!!signatoryPrefs[opt.key]}
                          tabIndex={0}
                          className="d-flex align-items-center gap-2 mb-3"
                          style={{
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            userSelect: 'none',
                            opacity: disabled ? 0.6 : 1
                          }}
                          onClick={() => {
                            if (disabled) return;
                            toggleSignatoryPref(opt.key);
                          }}
                          onKeyDown={(e) => {
                            if (disabled) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleSignatoryPref(opt.key);
                            }
                          }}
                        >
                          <input
                            id={opt.id}
                            type="checkbox"
                            checked={!!signatoryPrefs[opt.key]}
                            readOnly
                            disabled={disabled}
                            style={{
                              pointerEvents: 'none', // ensure ONE click path (container) toggles once
                              marginTop: 2
                            }}
                          />
                          <label
                            htmlFor={opt.id}
                            className="mb-0"
                            style={{ cursor: disabled ? 'not-allowed' : 'pointer', marginLeft: 4 }}
                          >
                            {opt.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </Tab>
              </Tabs>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={submitting || loading}
          >
            {submitting ? 'Updating...' : 'Update Project'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Project Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 mb-2">Confirm Project Update</h5>
            <p className="text-muted">Are you sure you want to save these changes?</p>
            {formData.fundSource === 'STF' || formData.fundSource === 'MDS' ? (
              <Alert variant="info" className="mt-3">
                <strong>Note:</strong> Since you selected STF or MDS as fund source, the Line-Item Budget form will open after saving for you to edit.
              </Alert>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleConfirmSubmit} disabled={submitting}>
            {submitting ? 'Updating...' : 'Update Project'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete LIB Confirmation Modal */}
      <Modal show={showDeleteLibModal} onHide={() => setShowDeleteLibModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Line-Item Budget</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 mb-2">Delete Line-Item Budget</h5>
            <p className="text-muted">
              Since STF or MDS has not been selected, the Line-Item Budget will be deleted.
            </p>
            <p className="text-muted mb-0">
              <strong>Are you sure you would like to delete the LIB data?</strong>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteLibModal(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteLibAndUpdate} disabled={submitting}>
            {submitting ? 'Deleting...' : 'Delete LIB'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Line-Item Budget Modal */}
      {showBudgetModal && (
        <LineItemBudgetModal
          show={showBudgetModal}
          onHide={() => {
            setShowBudgetModal(false);
            setUpdatedProjectData(null);
            // Close the edit modal after LIB modal is closed
            onHide();
            if (onSuccess) onSuccess();
          }}
          projectId={projectId}
          projectData={updatedProjectData}
          onSuccess={() => {
            setShowBudgetModal(false);
            setUpdatedProjectData(null);
            showSuccess('Line-Item Budget updated successfully!');
            // Close the edit modal after LIB is saved
            onHide();
            if (onSuccess) onSuccess();
          }}
        />
      )}
    </>
  );
};

export default ProjectProposalEditModal;
